import type { ReactNode } from 'react';

/**
 * Plain text with URLs turned into safe anchors, **double-asterisk**
 * runs rendered bold, and line breaks preserved. Used for staff-authored
 * guidance (e.g. screening-check instructions) where full rich text
 * would be overkill. Renders text nodes, never raw HTML.
 */
const URL_RE = /(https?:\/\/[^\s<>"')\]]+)/g;
const BOLD_RE = /(\*\*[^*]+\*\*)/g;

function linkify(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(URL_RE);
  return parts.map((part, i) =>
    // split() with a capturing group puts matches at odd indices.
    i % 2 === 1 ? (
      <a
        key={`${keyPrefix}${i}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all font-medium text-accent underline"
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}

export function LinkifiedText({ text, className }: { text: string; className?: string }) {
  const segments = text.split(BOLD_RE);
  return (
    <span className={`whitespace-pre-line ${className ?? ''}`.trim()}>
      {segments.map((seg, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-semibold">
            {linkify(seg.slice(2, -2), `${i}-`)}
          </strong>
        ) : (
          linkify(seg, `${i}-`)
        ),
      )}
    </span>
  );
}
